import { useCallback, useEffect, useState } from 'react'
import { useSnackbar } from '@tecsinapse/cortex-react'
import { HTTP_STATUS, SETUP_APP_ID, SETUP_CONFIG_ID } from '@web/modules/core'
import { IntegratedLoginVO } from '@repo/types'
import { RestChatService } from '@repo/network'

declare const window: any

export const useWingoSetup = () => {
  const [wabaId, setWabaId] = useState<string>()
  const [phoneNumberId, setPhoneNumberId] = useState<string>()
  const [token, setToken] = useState<string | undefined>()
  const [hasBusinessApp, setHasBusinessApp] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState(false)
  const { snackbar } = useSnackbar()

  const FINISH_ONBOARDING = 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
  const FINISH = 'FINISH'

  const loginCallback = useCallback((response: any) => {
    setSubmitting(false)
    if (response.authResponse) {
      const code = response.authResponse.code
      setToken(code)
    }
  }, [])

  const handleLogin = useCallback(() => {
    if (!window.FB) {
      return
    }

    setSubmitting(true)
    window.FB.login(loginCallback, {
      config_id: SETUP_CONFIG_ID,
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        setup: {},
        featureType: 'whatsapp_business_app_onboarding',
        sessionInfoVersion: '3'
      }
    })
  }, [loginCallback])

  const sendLoginInfo = useCallback(
    async (wabaId: string, token: string, hasBusinessApp: boolean, phoneNumberId?: string) => {
      try {
        setSubmitting(true)
        const info: IntegratedLoginVO = { wabaId, phoneNumberId, token, hasBusinessApp }
        const responseStatus = await RestChatService.integratedLogin(info)

        if (responseStatus === HTTP_STATUS.OK) {
          setSubmitting(false)
          const successMessage = 'Cadastro efetuado, a Tecsinapse agradece!'

          snackbar.show('success', successMessage, { position: 'top-center' })
        } else {
          throw new Error(responseStatus)
        }
      } catch (e: any) {
        setSubmitting(false)

        snackbar.show('error', 'Ops! Algo deu errado!', { position: 'top-center' })
        console.error(`Integrated login failed, wabaId: ${wabaId} | phoneNumber: ${phoneNumberId}`)
      }
    },
    [snackbar]
  )

  // eslint-disable-next-line no-useless-escape
  const isLikelyJson = (str: any) => typeof str === 'string' && /^[\[{]/.test(str.trim())

  const isLikelyQueryString = (str: any) => {
    return typeof str === 'string' && str.trim().includes('=') && !/\s/.test(str.trim())
  }

  const handleParseData = useCallback((eventData: any) => {
    try {
      if (isLikelyJson(eventData)) {
        return JSON.parse(eventData.trim())
      }

      if (isLikelyQueryString(eventData)) {
        return Object.fromEntries(new URLSearchParams(eventData.trim()))
      }
    } catch (error) {
      console.error('Parse data error', error)
      return eventData
    }
  }, [])

  const handleSetLoginInfo = useCallback((data: any, event: string) => {
    const { waba_id: wabaId, phone_number_id: phoneNumberId } = data
    console.log('log data')
    console.log(data)
    if (!wabaId && !phoneNumberId) {
      return
    }

    setHasBusinessApp(event === FINISH_ONBOARDING)
    setPhoneNumberId(phoneNumberId)
    setWabaId(wabaId)
  }, [])

  const handleProcessMessage = useCallback(
    (event: any) => {
      const FACEBOOK_ORIGINS = [
        'https://www.facebook.com',
        'https://web.facebook.com',
        'https://wingochat.tecsinapse.com'
      ]
      const FACEBOOK_LOGIN_EVENTS = [FINISH_ONBOARDING, FINISH, 'https://wingochat.tecsinapse.com']

      if (!FACEBOOK_ORIGINS.includes(event.origin)) {
        return
      }

      const eventData = handleParseData(event.data)
      const { data, event: evt } = eventData

      if (FACEBOOK_LOGIN_EVENTS.includes(evt)) {
        handleSetLoginInfo(data, evt)
      }
    },
    [handleParseData, handleSetLoginInfo]
  )

  useEffect(() => {
    window.addEventListener('message', handleProcessMessage)

    const script = document.createElement('script')

    script.src = 'https://connect.facebook.net/pt_BR/sdk.js#xfbml=1&version=v23.0&appId=3681040948823374'
    script.async = true
    script.crossOrigin = 'anonymous'

    document.head.appendChild(script)

    if (window.FB) {
      window.FB.init({
        appId: SETUP_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v24.0'
      })
    }

    return () => {
      document.head.removeChild(script)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token && wabaId) {
      sendLoginInfo(wabaId, token, hasBusinessApp, phoneNumberId).then()
    }
  }, [token, wabaId, phoneNumberId, hasBusinessApp, sendLoginInfo])

  return { handleLogin, submitting }
}
